import { z } from "zod";
import { localeSchema } from "./common.js";

/**
 * Pure-data template registry for the Hyperframes pipeline.
 *
 * After the migration off Remotion, templates no longer ship a React
 * `component` — the actual render logic lives in `src/hyperframes/templates/`.
 * What stays here are the schemas, defaults, durations, and wizard fields
 * consumed by the API, CLI scripts, and the AI generator.
 */

export type TemplateId =
  | "HookReveal"
  | "ProblemSolution"
  | "BeforeAfter"
  | "ListBanger"
  | "TestimonialCard"
  | "FeatureShowcase"
  | "BigStatement"
  | "CountdownCTA"
  | "PatternInterrupt";

export type FieldKind = "text" | "longtext" | "list" | "number" | "ctaKey";

export type FieldDefinition = {
  key: string;
  label: string;
  hint?: string;
  kind: FieldKind;
  options?: string[];
  required?: boolean;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
export type TemplateEntry = {
  id: TemplateId;
  label: string;
  description: string;
  category: string;
  schema: z.ZodType<any>;
  defaultProps: Record<string, any>;
  durationSeconds: (props: any) => number;
  fields: FieldDefinition[];
};
/* eslint-enable @typescript-eslint/no-explicit-any */

const ctaKeyOptions = ["tryFree", "learnMore", "joinNow", "getStarted", "watchDemo", "talkToUs"];

// ---------- Schemas (inlined; the legacy React files have been removed) ----------

export const hookRevealSchema = z.object({
  hook: z.string().min(2),
  payoff: z.string().min(2),
  cta: z.string().default(""),
  ctaKey: z.enum(["tryFree", "learnMore", "joinNow", "getStarted", "watchDemo", "talkToUs"]).default("tryFree"),
  locale: localeSchema.default("de"),
});
export type HookRevealProps = z.infer<typeof hookRevealSchema>;
export const hookRevealDefault: HookRevealProps = {
  hook: "Niemand redet darüber, aber das ändert alles.",
  payoff: "In 15 Sekunden zeigen wir dir, warum das gerade jetzt zählt.",
  cta: "",
  ctaKey: "tryFree",
  locale: "de",
};

export const problemSolutionSchema = z.object({
  problem: z.string(),
  solution: z.string(),
  cta: z.string().default(""),
  ctaKey: z.enum(["tryFree", "learnMore", "joinNow", "getStarted"]).default("tryFree"),
  locale: localeSchema.default("de"),
});
export type ProblemSolutionProps = z.infer<typeof problemSolutionSchema>;
export const problemSolutionDefault: ProblemSolutionProps = {
  problem: "Du verlierst Stunden mit immer demselben Workflow.",
  solution: "Ein Klick – und es passiert von selbst.",
  cta: "",
  ctaKey: "tryFree",
  locale: "de",
};

export const beforeAfterSchema = z.object({
  before: z.string(),
  after: z.string(),
  beforeMetric: z.string().default(""),
  afterMetric: z.string().default(""),
  cta: z.string().default(""),
  ctaKey: z.enum(["tryFree", "learnMore", "getStarted", "watchDemo"]).default("getStarted"),
  locale: localeSchema.default("de"),
});
export type BeforeAfterProps = z.infer<typeof beforeAfterSchema>;
export const beforeAfterDefault: BeforeAfterProps = {
  before: "6 Stunden manuelles Sortieren",
  after: "6 Sekunden mit einem Klick",
  beforeMetric: "6h",
  afterMetric: "6s",
  cta: "",
  ctaKey: "getStarted",
  locale: "de",
};

export const listBangerSchema = z.object({
  hook: z.string(),
  items: z.array(z.string()).min(2).max(5),
  cta: z.string().default(""),
  ctaKey: z.enum(["learnMore", "tryFree", "joinNow", "getStarted"]).default("learnMore"),
  locale: localeSchema.default("de"),
});
export type ListBangerProps = z.infer<typeof listBangerSchema>;
export const listBangerDefault: ListBangerProps = {
  hook: "3 Fehler, die fast alle machen",
  items: ["Du startest ohne klares Ziel.", "Du misst die falschen Zahlen.", "Du gibst zu früh auf."],
  cta: "",
  ctaKey: "learnMore",
  locale: "de",
};

export const testimonialSchema = z.object({
  quote: z.string(),
  persona: z.string(),
  role: z.string().default(""),
  result: z.string().default(""),
  rating: z.number().int().min(1).max(5).default(5),
  cta: z.string().default(""),
  ctaKey: z.enum(["learnMore", "tryFree", "talkToUs", "joinNow"]).default("learnMore"),
  locale: localeSchema.default("de"),
});
export type TestimonialProps = z.infer<typeof testimonialSchema>;
export const testimonialDefault: TestimonialProps = {
  quote: "Ich habe 10 Stunden pro Woche zurückgewonnen.",
  persona: "Sara M.",
  role: "Founder · Berlin",
  result: "+38% Umsatz in 60 Tagen",
  rating: 5,
  cta: "",
  ctaKey: "learnMore",
  locale: "de",
};

export const featureSchema = z.object({
  title: z.string(),
  benefits: z.array(z.string()).min(1).max(4),
  feature: z.string().default("Das eine Feature"),
  cta: z.string().default(""),
  ctaKey: z.enum(["tryFree", "learnMore", "watchDemo", "getStarted"]).default("watchDemo"),
  locale: localeSchema.default("de"),
});
export type FeatureProps = z.infer<typeof featureSchema>;
export const featureDefault: FeatureProps = {
  title: "Ein Feature, das alles ändert",
  feature: "Auto-Sync",
  benefits: ["Spart 6h pro Woche", "Funktioniert mit deinen Tools", "Setup in 60 Sekunden"],
  cta: "",
  ctaKey: "watchDemo",
  locale: "de",
};

export const bigStatementSchema = z.object({
  statement: z.string(),
  proof: z.string().default(""),
  bigNumber: z.string().default(""),
  cta: z.string().default(""),
  ctaKey: z.enum(["tryFree", "learnMore", "joinNow", "getStarted", "watchDemo"]).default("learnMore"),
  locale: localeSchema.default("de"),
});
export type BigStatementProps = z.infer<typeof bigStatementSchema>;
export const bigStatementDefault: BigStatementProps = {
  statement: "97% wissen das nicht.",
  proof: "Und du gehörst gleich zu den 3%.",
  bigNumber: "97%",
  cta: "",
  ctaKey: "learnMore",
  locale: "de",
};

export const countdownSchema = z.object({
  hook: z.string(),
  offer: z.string(),
  startNumber: z.number().int().min(3).max(99).default(10),
  cta: z.string().default(""),
  ctaKey: z.enum(["joinNow", "getStarted", "tryFree", "talkToUs"]).default("joinNow"),
  locale: localeSchema.default("de"),
});
export type CountdownProps = z.infer<typeof countdownSchema>;
export const countdownDefault: CountdownProps = {
  hook: "Letzte Chance",
  offer: "Bonus-Bundle gratis – nur heute",
  startNumber: 10,
  cta: "",
  ctaKey: "joinNow",
  locale: "de",
};

export const patternInterruptSchema = z.object({
  stop: z.string().default("STOPP"),
  followUp: z.string(),
  payoff: z.string(),
  cta: z.string().default(""),
  ctaKey: z.enum(["learnMore", "tryFree", "joinNow", "getStarted"]).default("learnMore"),
  locale: localeSchema.default("de"),
});
export type PatternInterruptProps = z.infer<typeof patternInterruptSchema>;
export const patternInterruptDefault: PatternInterruptProps = {
  stop: "STOPP",
  followUp: "Wenn du das machst, ruinierst du es.",
  payoff: "So machst du es richtig.",
  cta: "",
  ctaKey: "learnMore",
  locale: "de",
};

// ---------- Registry ----------

export const TEMPLATES: Record<TemplateId, TemplateEntry> = {
  HookReveal: {
    id: "HookReveal",
    label: "Hook → Tease → Payoff → CTA",
    description: "Klassischer Hook-Reveal mit Brand-Logo. Bestes Allzweck-Format.",
    category: "Hook",
    schema: hookRevealSchema,
    defaultProps: hookRevealDefault,
    durationSeconds: () => 8,
    fields: [
      { key: "hook", label: "Hook (erste 1-2 Sekunden)", kind: "text", required: true },
      { key: "payoff", label: "Payoff (was kommt nach dem Hook)", kind: "text", required: true },
      { key: "cta", label: "CTA-Text (leer = Locale-Default)", kind: "text" },
      { key: "ctaKey", label: "CTA-Stil", kind: "ctaKey", options: ctaKeyOptions },
    ],
  },
  ProblemSolution: {
    id: "ProblemSolution",
    label: "Problem → Lösung → CTA",
    description: "Schmerzpunkt direkt benennen, danach die Lösung als kontrastierende Karte.",
    category: "Conversion",
    schema: problemSolutionSchema,
    defaultProps: problemSolutionDefault,
    durationSeconds: () => 9,
    fields: [
      { key: "problem", label: "Problem", kind: "text", required: true },
      { key: "solution", label: "Lösung", kind: "text", required: true },
      { key: "cta", label: "CTA-Text", kind: "text" },
      { key: "ctaKey", label: "CTA-Stil", kind: "ctaKey", options: ctaKeyOptions },
    ],
  },
  BeforeAfter: {
    id: "BeforeAfter",
    label: "Vorher / Nachher",
    description: "Side-by-Side Vergleich mit großen Metriken. Funktioniert visuell sofort.",
    category: "Transformation",
    schema: beforeAfterSchema,
    defaultProps: beforeAfterDefault,
    durationSeconds: () => 9,
    fields: [
      { key: "before", label: "Vorher (Zustand/Schmerz)", kind: "text", required: true },
      { key: "after", label: "Nachher (Zustand/Win)", kind: "text", required: true },
      { key: "beforeMetric", label: "Vorher-Metrik (z.B. '6h')", kind: "text" },
      { key: "afterMetric", label: "Nachher-Metrik (z.B. '6s')", kind: "text" },
      { key: "cta", label: "CTA-Text", kind: "text" },
      { key: "ctaKey", label: "CTA-Stil", kind: "ctaKey", options: ctaKeyOptions },
    ],
  },
  ListBanger: {
    id: "ListBanger",
    label: "Listicle (3 / 5 Punkte)",
    description: "Numerierte Liste mit animierten Karten. Klassisches TikTok/Reels-Format.",
    category: "Education",
    schema: listBangerSchema,
    defaultProps: listBangerDefault,
    durationSeconds: (p) => 1.5 + (p as ListBangerProps).items.length * 2 + 2.5,
    fields: [
      { key: "hook", label: "Listentitel / Hook", kind: "text", required: true },
      { key: "items", label: "Punkte (Komma-getrennt oder mehrzeilig)", kind: "list", required: true },
      { key: "cta", label: "CTA-Text", kind: "text" },
      { key: "ctaKey", label: "CTA-Stil", kind: "ctaKey", options: ctaKeyOptions },
    ],
  },
  TestimonialCard: {
    id: "TestimonialCard",
    label: "Testimonial / Social Proof",
    description: "Zitat-Karte mit Avatar, Sternen und Resultat-Badge.",
    category: "Social Proof",
    schema: testimonialSchema,
    defaultProps: testimonialDefault,
    durationSeconds: () => 9,
    fields: [
      { key: "quote", label: "Zitat", kind: "longtext", required: true },
      { key: "persona", label: "Person", kind: "text", required: true },
      { key: "role", label: "Rolle / Standort", kind: "text" },
      { key: "result", label: "Resultat-Badge", kind: "text" },
      { key: "rating", label: "Sterne (1-5)", kind: "number" },
      { key: "cta", label: "CTA-Text", kind: "text" },
      { key: "ctaKey", label: "CTA-Stil", kind: "ctaKey", options: ctaKeyOptions },
    ],
  },
  FeatureShowcase: {
    id: "FeatureShowcase",
    label: "Feature-Showcase",
    description: "Feature mit 3 Benefit-Punkten. Direkter Conversion-Treiber.",
    category: "Product",
    schema: featureSchema,
    defaultProps: featureDefault,
    durationSeconds: (p) => 1 + (p as FeatureProps).benefits.length * 1.5 + 4,
    fields: [
      { key: "feature", label: "Feature-Name (Badge)", kind: "text", required: true },
      { key: "title", label: "Titel", kind: "text", required: true },
      { key: "benefits", label: "Benefits (Komma-getrennt)", kind: "list", required: true },
      { key: "cta", label: "CTA-Text", kind: "text" },
      { key: "ctaKey", label: "CTA-Stil", kind: "ctaKey", options: ctaKeyOptions },
    ],
  },
  BigStatement: {
    id: "BigStatement",
    label: "Big Statement / Big Number",
    description: "Eine riesige Zahl + ein starker Satz. Perfekter Authority-Hook.",
    category: "Authority",
    schema: bigStatementSchema,
    defaultProps: bigStatementDefault,
    durationSeconds: () => 8,
    fields: [
      { key: "bigNumber", label: "Große Zahl (z.B. '97%' oder '10x')", kind: "text" },
      { key: "statement", label: "Statement", kind: "text", required: true },
      { key: "proof", label: "Beweis-Zeile", kind: "text" },
      { key: "cta", label: "CTA-Text", kind: "text" },
      { key: "ctaKey", label: "CTA-Stil", kind: "ctaKey", options: ctaKeyOptions },
    ],
  },
  CountdownCTA: {
    id: "CountdownCTA",
    label: "Countdown / FOMO-CTA",
    description: "Countdown + Knappheits-Botschaft. Action-Stage des Funnels.",
    category: "Action",
    schema: countdownSchema,
    defaultProps: countdownDefault,
    durationSeconds: () => 8,
    fields: [
      { key: "hook", label: "Hook (z.B. 'Letzte Chance')", kind: "text", required: true },
      { key: "offer", label: "Angebot", kind: "text", required: true },
      { key: "startNumber", label: "Countdown-Startzahl", kind: "number" },
      { key: "cta", label: "CTA-Text", kind: "text" },
      { key: "ctaKey", label: "CTA-Stil", kind: "ctaKey", options: ctaKeyOptions },
    ],
  },
  PatternInterrupt: {
    id: "PatternInterrupt",
    label: "Pattern Interrupt (STOPP)",
    description: "Aggressiver Scroll-Stopper. Flash + Shake. Sehr stark in Reels.",
    category: "Hook",
    schema: patternInterruptSchema,
    defaultProps: patternInterruptDefault,
    durationSeconds: () => 8,
    fields: [
      { key: "stop", label: "Stop-Wort (z.B. 'STOPP')", kind: "text" },
      { key: "followUp", label: "Folgesatz nach STOPP", kind: "text", required: true },
      { key: "payoff", label: "Payoff (Lösung/Tipp)", kind: "text", required: true },
      { key: "cta", label: "CTA-Text", kind: "text" },
      { key: "ctaKey", label: "CTA-Stil", kind: "ctaKey", options: ctaKeyOptions },
    ],
  },
};

export const TEMPLATE_IDS = Object.keys(TEMPLATES) as TemplateId[];

export function getTemplate(id: TemplateId): TemplateEntry {
  const t = TEMPLATES[id];
  if (!t) throw new Error(`Unknown template id: ${id}`);
  return t;
}
